import { tz, tzOffset } from '@date-fns/tz';
import { addDays, format } from 'date-fns';
import { z } from 'zod';

import { parseInstant } from './format';
import { formatOffset, zoneName } from './time-zone';

/*
 * <input type="datetime-local"> holds a wall-clock time with no zone. In the admin that wall time is
 * always in a market's zone (the record's own market), never the browser's, and the Admin API takes
 * UTC instants. A wall time can fail to name exactly one instant when the clocks change: in the
 * spring gap it does not exist, and in the autumn overlap it happens twice. Both are surfaced to the
 * staff member rather than silently moved.
 */

const INPUT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;
const INPUT_PATTERN = "yyyy-MM-dd'T'HH:mm";
const MINUTE_MS = 60_000;
const DAY_MS = 24 * 60 * MINUTE_MS;

/** Which of two identical wall times an ambiguous input means. */
export type Occurrence = 'earlier' | 'later';

/** The form value of an ambiguity choice: null until the staff member picks one. */
export const occurrenceSchema = z.enum(['earlier', 'later']).nullable();

export type MarketInput =
  /** Empty or not a date and time. */
  | { kind: 'invalid' }
  | { kind: 'ok'; iso: string }
  /** The wall time does not exist in the zone: the clocks jump from `from` to `to` at `transition`. */
  | { kind: 'gap'; time: string; date: string; from: string; to: string; transition: string }
  /** The wall time happens twice; the offsets are those in force at each instant. */
  | {
      kind: 'ambiguous';
      time: string;
      earlierIso: string;
      laterIso: string;
      earlierOffset: string;
      laterOffset: string;
    };

/** The datetime-local value (whole minutes) showing an instant in the market zone. */
export function toMarketInput(instant: string | Date | null | undefined, zone: string): string {
  const date = parseInstant(instant);
  return date ? format(date, INPUT_PATTERN, { in: tz(zone) }) : '';
}

/** The datetime-local value for now in the market zone, moved by whole days: a default window. */
export function marketNowInput(zone: string, days = 0, now: Date = new Date()): string {
  return format(addDays(now, days, { in: tz(zone) }), INPUT_PATTERN, { in: tz(zone) });
}

/** HH:mm of a wall time held as UTC fields. */
function wallTime(utcFields: number): string {
  return new Date(utcFields).toISOString().slice(11, 16);
}

/** The UTC instant(s) a market wall time names. */
export function fromMarketInput(value: string, zone: string): MarketInput {
  const match = INPUT.exec(value);
  if (!match) return { kind: 'invalid' };

  const part = (index: number) => Number(match[index] ?? 0);
  const [year, month, day, hour, minute, second] = [1, 2, 3, 4, 5, 6].map(part) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  // The wall time as if it were UTC: its fields, free of any zone.
  const wall = Date.UTC(year, month - 1, day, hour, minute, second);
  const check = new Date(wall);
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day ||
    check.getUTCHours() !== hour
  )
    return { kind: 'invalid' };

  // A day either side covers any single clock change; each offset gives a candidate instant, which
  // is real only if that offset is the one in force at it.
  const offsets = [...new Set([-DAY_MS, DAY_MS].map((d) => tzOffset(zone, new Date(wall + d))))];
  const instants = [
    ...new Set(
      offsets
        .map((offset) => wall - offset * MINUTE_MS)
        .filter((instant, i) => tzOffset(zone, new Date(instant)) === offsets[i]),
    ),
  ].sort((a, b) => a - b);

  const [earlier, later] = instants;
  if (earlier !== undefined && later === undefined)
    return { kind: 'ok', iso: new Date(earlier).toISOString() };

  const time = wallTime(wall);
  if (earlier !== undefined && later !== undefined) {
    return {
      kind: 'ambiguous',
      time,
      earlierIso: new Date(earlier).toISOString(),
      laterIso: new Date(later).toISOString(),
      earlierOffset: formatOffset(tzOffset(zone, new Date(earlier))),
      laterOffset: formatOffset(tzOffset(zone, new Date(later))),
    };
  }

  // A gap: the clocks moved forward from `before` to `after` somewhere between the two candidate
  // instants. That minute is read from the zone data rather than assumed.
  const before = Math.min(...offsets);
  const after = Math.max(...offsets);
  let low = wall - after * MINUTE_MS;
  let high = wall - before * MINUTE_MS;
  while (high - low > MINUTE_MS) {
    const mid = low + Math.floor((high - low) / 2 / MINUTE_MS) * MINUTE_MS;
    if (tzOffset(zone, new Date(mid)) === before) low = mid;
    else high = mid;
  }
  return {
    kind: 'gap',
    time,
    date: format(check, 'd MMM yyyy', { in: tz('UTC') }),
    from: wallTime(high + before * MINUTE_MS),
    to: wallTime(high + after * MINUTE_MS),
    transition: new Date(high).toISOString(),
  };
}

/**
 * The instant a date-time field stands for. An untouched field (its value still shows `original`
 * in the zone) gives back `original` exactly as received, seconds included: editing a record's
 * name must never move its dates.
 */
export function instantFor(
  original: string | null | undefined,
  input: string,
  zone: string,
): MarketInput {
  if (original && input === toMarketInput(original, zone)) return { kind: 'ok', iso: original };
  return fromMarketInput(input, zone);
}

/** The instant to send, or null while the input is invalid, in a gap, or an unchosen overlap. */
export function resolveMarketInput(
  input: MarketInput,
  occurrence: Occurrence | null,
): string | null {
  if (input.kind === 'ok') return input.iso;
  if (input.kind === 'ambiguous' && occurrence)
    return occurrence === 'earlier' ? input.earlierIso : input.laterIso;
  return null;
}

/** The instant to send for a validated field: `original` when untouched, else the entered time. */
export function fieldInstant(
  original: string | null | undefined,
  input: string,
  occurrence: Occurrence | null,
  zone: string,
): string | null {
  return resolveMarketInput(instantFor(original, input, zone), occurrence);
}

/** The instant whose offset describes an input: the entered time when there is one, else now. */
export function marketInputInstant(input: MarketInput): Date {
  switch (input.kind) {
    case 'ok':
      return new Date(input.iso);
    case 'ambiguous':
      return new Date(input.earlierIso);
    case 'gap':
      return new Date(input.transition);
    default:
      return new Date();
  }
}

/** "02:30 doesn't exist on 27 Mar 2027 in Palestine time: clocks move from 02:00 to 03:00. …" */
export function gapMessage(input: Extract<MarketInput, { kind: 'gap' }>, zone: string): string {
  return `${input.time} doesn't exist on ${input.date} in ${zoneName(zone)}: clocks move from ${input.from} to ${input.to}. Choose another time.`;
}

/**
 * Checks a datetime-local field inside a schema's superRefine and returns its instant, or null.
 * A gap is an error on the field; an overlap without a choice is an error on the choice.
 */
export function refineMarketInput(
  ctx: z.RefinementCtx,
  field: {
    path: string;
    occurrencePath: string;
    input: string;
    occurrence: Occurrence | null;
    original?: string | null;
  },
  zone: string,
): Date | null {
  if (!field.input) return null;
  const result = instantFor(field.original, field.input, zone);
  if (result.kind === 'invalid')
    ctx.addIssue({ code: 'custom', path: [field.path], message: 'Enter a date and time.' });
  else if (result.kind === 'gap')
    ctx.addIssue({ code: 'custom', path: [field.path], message: gapMessage(result, zone) });
  else if (result.kind === 'ambiguous' && !field.occurrence)
    ctx.addIssue({
      code: 'custom',
      path: [field.occurrencePath],
      message: `Choose which ${result.time} you mean.`,
    });

  const iso = resolveMarketInput(result, field.occurrence);
  return iso ? new Date(iso) : null;
}
